/**
 * Per-page SEO keyword clusters for NetoVibe.
 * Master-level keyword weaving across English and German markets.
 */

const BRAND_TERMS = ["NetoVibe", "NetoVibe agency", "NetoVibe web design"];

export const KEYWORDS = {
  en: {
    home: [
      ...BRAND_TERMS,
      "affordable web design agency",
      "custom website development",
      "freelance web designer near me",
      "professional business websites",
      "small business website design",
      "web design agency Germany",
      "bilingual website designer",
      "SEO web design",
      "landing page design agency",
      "responsive web development",
      "high-converting website design",
      "custom coded website",
      "modern web design studio",
      "hire a web designer",
    ],
    about: [
      ...BRAND_TERMS,
      "freelance web designer",
      "hybrid web design agency",
      "senior web developer",
      "boutique web design studio",
      "custom website developer",
      "web design freelancer Germany",
      "professional web design partner",
      "personal web designer",
    ],
    portfolio: [
      ...BRAND_TERMS,
      "web design portfolio",
      "cleaning company website design",
      "Gebäudereinigung website",
      "local business website case study",
      "custom website case study",
      "small business web design examples",
      "professional cleaning website",
      "German cleaning service website",
      "Penzberg web design",
      "responsive local business website",
      "mobile-first website design",
    ],
    pricing: [
      ...BRAND_TERMS,
      "affordable website pricing",
      "cheap web design packages",
      "small business website cost",
      "web design price list",
      "landing page pricing",
      "how much does a website cost",
      "flat-rate web design",
      "affordable web design Germany",
      "professional business website cost",
      "custom website development price",
      "5 page website cost",
      "website maintenance cost",
    ],
    contact: [
      ...BRAND_TERMS,
      "contact web design agency",
      "get a website quote",
      "hire freelance web designer",
      "website design consultation",
      "custom website quote",
      "web designer contact",
      "book a web designer",
      "request website quote",
      "small business web design contact",
    ],
    legal: [...BRAND_TERMS, "NetoVibe imprint", "NetoVibe privacy policy"],
  },

  de: {
    home: [
      ...BRAND_TERMS,
      "Günstige Webdesign Agentur",
      "Professionelle Homepage Erstellung",
      "Webdesigner für Firmen",
      "Website erstellen lassen",
      "Webdesign Berlin",
      "Homepage erstellen lassen günstig",
      "Firmenwebsite erstellen",
      "kleine Unternehmen Webdesign",
      "Webseite gestalten lassen",
      "Webagentur für Mittelstand",
      "responsive Homepage",
      "mobile-optimierte Website",
      "SEO Webdesign",
      "Landingpage erstellen lassen",
      "individuelle Website programmieren",
    ],
    about: [
      ...BRAND_TERMS,
      "Webdesigner Freelancer",
      "hybride Webdesign Agentur",
      "Webentwickler Berlin",
      "Boutique Webdesign Studio",
      "persönlicher Webdesigner",
      "Webdesign Partner für Firmen",
    ],
    portfolio: [
      ...BRAND_TERMS,
      "Webdesign Referenzen",
      "Gebäudereinigung Website",
      "Homepage für Reinigungsfirma",
      "lokale Firma Website Beispiel",
      "Zemka Case Study",
      "Firmenhomepage Beispiel",
      "Webseite für Reinigungsdienst",
      "Penzberg Webdesign",
      "mobile Website Referenz",
    ],
    pricing: [
      ...BRAND_TERMS,
      "Webseite Preise",
      "günstige Webdesign Pakete",
      "Homepage Kosten",
      "Website erstellen lassen Kosten",
      "Landingpage Preis",
      "Was kostet eine Homepage",
      "Webdesign Festpreis",
      "professionelle Firmenwebsite Kosten",
      "5 Seiten Website Preis",
      "Wartung Website Kosten",
    ],
    contact: [
      ...BRAND_TERMS,
      "Webdesign Agentur Kontakt",
      "Website Angebot anfordern",
      "Webdesigner anfragen",
      "Website erstellen lassen anfragen",
      "individuelles Webdesign Angebot",
      "Webdesigner beauftragen",
      "Beratungstermin Webdesign",
    ],
    legal: [...BRAND_TERMS, "NetoVibe Impressum", "NetoVibe Datenschutz"],
  },
};

export const FAQ = {
  en: [
    {
      q: "How much does a professional business website cost?",
      a: "NetoVibe offers three transparent packages: Basic Launch Pad from €600 (single-page landing), Professional Business at €1,000 (up to 5 pages, best value), and Corporate Premium at €1,500 (multi-page, bilingual, VIP support). No hidden fees, no monthly lock-in — one clear invoice.",
    },
    {
      q: "How long does it take to build my website?",
      a: "A Basic Launch Pad ships in about 7 days, Professional Business in 2–3 weeks, and Corporate Premium in 4–5 weeks depending on scope. You always get a firm delivery date before we start.",
    },
    {
      q: "Do you build bilingual websites in English and German?",
      a: "Yes. Every NetoVibe project is structured for bilingual delivery from day one. The Corporate Premium package includes a full dual-language setup with hreflang alternates for international SEO.",
    },
    {
      q: "Do you handle SEO on new websites?",
      a: "Yes. Every build includes semantic HTML, per-page meta titles and descriptions, canonical tags, hreflang for multi-language sites, a compliant sitemap and robots.txt — the foundations Google actually rewards.",
    },
    {
      q: "Can I edit the website myself after launch?",
      a: "Absolutely. Every site ships with a simple handover, either via a lightweight CMS or documented code, so you can update text, images and prices without touching a developer.",
    },
  ],
  de: [
    {
      q: "Was kostet eine professionelle Firmenwebsite?",
      a: "NetoVibe bietet drei transparente Pakete: Basic Launch Pad ab 600 € (einseitige Landingpage), Professional Business für 1.000 € (bis zu 5 Seiten, beliebteste Wahl) und Corporate Premium für 1.500 € (mehrseitig, zweisprachig, VIP-Support). Keine versteckten Kosten, keine monatliche Bindung.",
    },
    {
      q: "Wie lange dauert die Erstellung meiner Website?",
      a: "Ein Basic Launch Pad ist in ca. 7 Tagen live, Professional Business in 2–3 Wochen, Corporate Premium in 4–5 Wochen — abhängig vom Umfang. Sie erhalten immer ein festes Launch-Datum vor Projektstart.",
    },
    {
      q: "Erstellen Sie zweisprachige Websites in Deutsch und Englisch?",
      a: "Ja. Jedes NetoVibe-Projekt ist ab Tag eins für zweisprachige Auslieferung vorbereitet. Das Paket Corporate Premium beinhaltet ein vollständiges DE/EN-Setup mit hreflang-Alternates für internationales SEO.",
    },
    {
      q: "Kümmern Sie sich um SEO bei neuen Websites?",
      a: "Ja. Jeder Launch enthält semantisches HTML, Meta-Titel und Beschreibungen pro Seite, Canonical Tags, hreflang für Mehrsprachigkeit sowie eine saubere Sitemap und robots.txt — die Grundlagen, die Google tatsächlich honoriert.",
    },
    {
      q: "Kann ich die Website nach dem Launch selbst bearbeiten?",
      a: "Selbstverständlich. Jede Seite wird mit einer sauberen Übergabe ausgeliefert — entweder über ein schlankes CMS oder dokumentierten Code, sodass Sie Texte, Bilder und Preise ohne Entwickler pflegen können.",
    },
  ],
};

export { BRAND_TERMS };
